import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, Trash, PaperPlaneTilt, X, ShieldCheck, Warning } from "@phosphor-icons/react";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [templates, setTemplates] = useState([]);
  const [showSend, setShowSend] = useState(false);
  const [content, setContent] = useState("");
  const [tid, setTid] = useState("");
  const [extraVars, setExtraVars] = useState({});
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (start) params.start = start;
    if (end) params.end = end;
    const { data } = await api.get("/customers", { params });
    setCustomers(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, start, end]);

  useEffect(() => { api.get("/templates").then(r => setTemplates(r.data)); }, []);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === customers.length) setSelected(new Set());
    else setSelected(new Set(customers.map(c => c.id)));
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this customer?")) return;
    await api.delete(`/customers/${id}`);
    toast.success("Customer removed");
    setSelected(new Set());
    load();
  };

  const openSend = () => {
    if (selected.size === 0) return toast.error("Select at least one customer");
    setContent(""); setTid(""); setExtraVars({}); setShowSend(true);
  };
  const pickTemplate = (id) => {
    setTid(id);
    const t = templates.find(x => x.id === id);
    if (t) {
      setContent(t.content);
      const vars = {};
      (t.extra_var_labels || []).forEach((_, i) => { vars[i + 3] = ""; });
      setExtraVars(vars);
    } else {
      setExtraVars({});
    }
  };
  const currentTemplate = templates.find(t => t.id === tid);
  const isApproved = Boolean(currentTemplate?.content_sid);
  const missingVars = currentTemplate?.extra_var_labels?.some((_, i) => !extraVars[i + 3]?.trim());

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/messages/send", {
        customer_ids: Array.from(selected),
        content,
        template_id: tid || null,
        extra_vars: Object.keys(extraVars).length ? extraVars : null,
      });
      if (data.provider === "twilio" && data.used_content_sid && data.delivered_count > 0) {
        toast.success(`Sent to ${data.delivered_count} of ${data.recipients_count} via WhatsApp`);
      } else if (data.provider === "twilio" && data.delivered_count === 0) {
        toast.warning("Twilio rejected the send. See message history for details.", { duration: 5000 });
      } else {
        toast.success(`Sent to ${selected.size} customer${selected.size===1?"":"s"}`);
      }
      setShowSend(false); setSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  const anySelected = selected.size > 0;

  return (
    <div data-testid="customers-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-2">Your list</div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Customers</h1>
          <p className="text-brand-text/70 mt-1">Phone numbers are masked. Only you can see this list.</p>
        </div>
        <button onClick={openSend} disabled={!anySelected} data-testid="open-send-btn"
          className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:bg-brand-border disabled:text-brand-textMuted text-white rounded-full px-5 py-3 text-sm font-semibold transition-colors">
          <PaperPlaneTilt size={18} weight="duotone" /> Send message {anySelected && `(${selected.size})`}
        </button>
      </div>

      <div className="bg-white border border-brand-border rounded-2xl p-4 md:p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-textMuted" />
            <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search by name…" data-testid="search-customers"
              className="w-full h-11 pl-10 pr-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none" />
          </div>
          <input type="date" value={start} onChange={(e)=>setStart(e.target.value)} data-testid="filter-start"
            className="h-11 px-4 rounded-xl bg-brand-bg border border-brand-border" />
          <input type="date" value={end} onChange={(e)=>setEnd(e.target.value)} data-testid="filter-end"
            className="h-11 px-4 rounded-xl bg-brand-bg border border-brand-border" />
        </div>
      </div>

      <div className="bg-white border border-brand-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-bg border-b border-brand-border text-left">
              <tr>
                <th className="p-4"><input type="checkbox" checked={selected.size>0 && selected.size===customers.length} onChange={toggleAll} data-testid="select-all" /></th>
                <th className="p-4 font-heading font-semibold">Name</th>
                <th className="p-4 font-heading font-semibold">Phone</th>
                <th className="p-4 font-heading font-semibold hidden md:table-cell">Email</th>
                <th className="p-4 font-heading font-semibold hidden lg:table-cell">Preferences</th>
                <th className="p-4 font-heading font-semibold hidden md:table-cell">Joined</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-8 text-center text-brand-textMuted">Loading…</td></tr>
              ) : customers.length === 0 ? (
                <tr><td colSpan={7} className="p-12 text-center text-brand-textMuted">No customers yet. Share your QR to start collecting.</td></tr>
              ) : customers.map((c) => (
                <tr key={c.id} className="border-b border-brand-border hover:bg-brand-bg/60 transition-colors" data-testid={`row-${c.id}`}>
                  <td className="p-4"><input type="checkbox" checked={selected.has(c.id)} onChange={()=>toggle(c.id)} data-testid={`select-${c.id}`} /></td>
                  <td className="p-4 font-medium">{c.name}</td>
                  <td className="p-4 font-mono text-brand-text/80">{c.phone}</td>
                  <td className="p-4 hidden md:table-cell text-brand-text/80">{c.email || "—"}</td>
                  <td className="p-4 hidden lg:table-cell text-brand-text/80">{c.preferences || "—"}</td>
                  <td className="p-4 hidden md:table-cell text-brand-textMuted">{new Date(c.created_at).toLocaleDateString()}</td>
                  <td className="p-4 text-right">
                    <button onClick={()=>remove(c.id)} className="text-brand-error hover:opacity-80" data-testid={`del-${c.id}`}>
                      <Trash size={18} weight="duotone" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showSend && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 overflow-y-auto" data-testid="send-modal">
          <div className="bg-white w-full max-w-lg rounded-3xl border border-brand-border p-6 sm:p-8 my-8">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-2xl font-bold">Send WhatsApp</h3>
              <button onClick={()=>setShowSend(false)} data-testid="close-send"><X size={22} /></button>
            </div>
            <p className="text-sm text-brand-text/70 mb-5">Sending to {selected.size} customer{selected.size===1?"":"s"}.</p>

            <div className="mb-4">
              <label className="text-sm font-semibold">Choose an approved template</label>
              <select value={tid} onChange={(e)=>pickTemplate(e.target.value)} data-testid="send-template"
                className="mt-1.5 w-full h-11 px-4 rounded-xl bg-brand-bg border border-brand-border">
                <option value="">— None (freeform, 24-hour window only) —</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.content_sid ? "✓ " : ""}{t.name}{t.content_sid ? " (Approved)" : ""}
                  </option>
                ))}
              </select>
            </div>

            {tid && (
              <div className={`mb-4 rounded-xl border p-3 text-sm ${isApproved ? "bg-brand-success/10 border-brand-success/25 text-brand-text" : "bg-brand-warning/10 border-brand-warning/25 text-brand-text"}`} data-testid="template-status">
                {isApproved ? (
                  <div className="flex items-start gap-2">
                    <ShieldCheck size={18} weight="duotone" className="text-brand-success shrink-0 mt-0.5" />
                    <span><strong>Approved template.</strong> Will deliver as real WhatsApp to any customer.</span>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <Warning size={18} weight="duotone" className="text-brand-warning shrink-0 mt-0.5" />
                    <span><strong>No Content SID attached.</strong> Will only deliver inside a 24-hour session window. Add a SID in Templates for reliable send.</span>
                  </div>
                )}
              </div>
            )}

            {currentTemplate?.extra_var_labels?.length > 0 && (
              <div className="mb-4 space-y-3">
                <div className="text-sm font-semibold">Fill in the variables</div>
                <p className="text-xs text-brand-textMuted -mt-1">💡 Line breaks and emoji work! Press Enter for a new line, paste any 🎉 🛍️ 💰 emoji into the offer text.</p>
                {currentTemplate.extra_var_labels.map((label, i) => (
                  <div key={i}>
                    <label className="text-xs text-brand-textMuted flex items-center gap-2">
                      <code className="font-mono text-brand-secondary">{`{{${i+3}}}`}</code> {label}
                    </label>
                    <textarea value={extraVars[i + 3] || ""} onChange={(e)=>setExtraVars({...extraVars, [i+3]: e.target.value})}
                      data-testid={`var-${i+3}`} placeholder={label} rows={3} maxLength={800}
                      className="mt-1 w-full p-3 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none resize-y" />
                    <div className="mt-1 text-[10px] text-brand-textMuted text-right">{(extraVars[i + 3] || "").length}/800</div>
                  </div>
                ))}
              </div>
            )}

            <label className="text-sm font-semibold">Preview / Freeform body</label>
            <textarea value={content} onChange={(e)=>setContent(e.target.value)} rows={5} data-testid="send-content"
              className="mt-1.5 w-full p-4 rounded-xl bg-brand-bg border border-brand-border focus:border-brand-primary focus:ring-1 focus:ring-brand-primary outline-none"
              placeholder="Hey! We're running a 20% off weekend. Come say hi 🌿" />
            {isApproved && <p className="mt-1 text-xs text-brand-textMuted">This text is stored in history as preview. Real WhatsApp content comes from the approved template.</p>}

            <div className="mt-6 flex gap-3">
              <button onClick={()=>setShowSend(false)} className="flex-1 h-11 rounded-full border border-brand-border hover:bg-brand-bg font-semibold" data-testid="cancel-send">Cancel</button>
              <button onClick={send} disabled={sending || !content.trim() || missingVars}
                className="flex-1 h-11 rounded-full bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-white font-semibold" data-testid="confirm-send">
                {sending ? "Sending…" : "Send now"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
